import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useAuth } from "../auth/AuthContext";
import { hasAnyRole } from "../auth/authUi";
import { CMMS_FIELD_ROLES } from "../features/cmms/cmmsRoles";
import messages from "../features/cmmsWorkOrders/messages";
import { pushNotification } from "../utils/notifications";

// Deliberately NOT part of useOeeNotifications: a different hub, different groups and a
// different lifecycle. The OEE hub is per-page; this one is app-wide, because a
// technician should be told about a new work order wherever they happen to be.
const HUB_URL = "/hubs/cmms-notifications";

// Event names come from CmmsNotificationService.
const WORK_ORDER_CREATED = "WorkOrderCreated";
const WORK_ORDER_REOPENED = "WorkOrderReopened";
const WORK_ORDERS_CHANGED = "WorkOrdersChanged";

export function useCmmsNotifications(companyKey, enabled = true) {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { roleIds } = useAuth();

  const normalizedCompanyKey = companyKey != null ? Number(companyKey) : null;
  // Only users who can act on a work order need the connection at all.
  // `enabled` carries the CMMS module switch: with the module off no socket may be opened at
  // all, so this is checked inside the effect rather than by hiding the component.
  const isCmmsUser = enabled && hasAnyRole(roleIds, CMMS_FIELD_ROLES);

  // Keep the latest intl/queryClient available to the handlers without making them
  // part of the effect's dependencies — otherwise a locale switch would tear the
  // connection down and reconnect.
  const handlerRef = useRef();
  // The week and month overviews' projection (useProjectedOccurrences). When the sweep raises
  // a scheduled order, the order arrives in the lists while the cached projection still holds
  // the same occurrence, and an open calendar shows the work and its hours twice. The raw
  // companyKey, not the normalized one: the views key on the stored value as it is.
  const invalidateProjections = () => {
    queryClient.invalidateQueries({
      queryKey: ["cmmsRecurringSchedules", companyKey, { view: "projected" }],
    });
  };
  handlerRef.current = {
    onCreated: (payload) => {
      queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrders"] });
      // The sidebar badge. A new work order for my department changes the count, and this
      // invalidation is the only thing that moves it without a page reload.
      queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
      invalidateProjections();
      pushNotification(
        intl.formatMessage(messages.notificationCreated, { title: payload?.title ?? "" }),
      );
    },
    onReopened: (payload) => {
      queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrders"] });
      queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrder"] });
      // Reopening moves a work order back into Open, so it re-enters the /mine result.
      queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
      pushNotification(
        intl.formatMessage(messages.notificationReopened, { title: payload?.title ?? "" }),
      );
    },
    // No toast. A new order that went to a trade still sits in /mine for the department's
    // members outside the trade, and this is how their list and badge hear of it.
    onChanged: () => {
      queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrders"] });
      queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
      invalidateProjections();
    },
  };

  useEffect(() => {
    if (!normalizedCompanyKey || !isCmmsUser) return undefined;

    let cancelled = false;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL)
      .withAutomaticReconnect()
      .build();

    connection.on(WORK_ORDER_CREATED, (payload) => handlerRef.current.onCreated(payload));
    connection.on(WORK_ORDER_REOPENED, (payload) => handlerRef.current.onReopened(payload));
    connection.on(WORK_ORDERS_CHANGED, () => handlerRef.current.onChanged());

    // Group membership is per-connection, so it has to be re-established after every
    // reconnect, not just on the first start.
    //
    // Both calls resolve the caller server-side from the connection's identity. The
    // client neither knows nor names its department ids: it used to fetch them and hand
    // them straight back, which meant anyone could have asked for any department.
    const joinGroups = async () => {
      await connection.invoke("JoinUserAsync");
      await connection.invoke("JoinDepartmentsAsync");
    };

    connection.onreconnected(() => {
      joinGroups().catch(console.error);
    });

    const start = async () => {
      await connection.start();
      if (cancelled) return;
      await joinGroups();
    };

    start().catch(console.error);

    return () => {
      cancelled = true;
      connection.stop();
    };
  }, [normalizedCompanyKey, isCmmsUser]);
}

export default useCmmsNotifications;
